import {
  Building2,
  Users,
  Calendar,
  CreditCard,
  KeyRound,
  Printer,
  UserCircle,
  Globe,
  UserCheck,
  ArrowRight,
  Check,
  Zap,
  Layers,
  Phone,
  Sparkles,
} from 'lucide-react';

const SIGNUP_URL = 'https://app.theonespace.app/signup';
const LOGIN_URL = 'https://app.theonespace.app/login';

const FEATURES = [
  {
    icon: Users,
    title: 'Member Lifecycle',
    description: 'Onboard, pause, resume, and cancel members with Stripe billing automated end-to-end. New members get billing, door access, and printing in one flow.',
  },
  {
    icon: Calendar,
    title: 'Room & Desk Booking',
    description: 'Interactive calendar view for conference rooms, phone booths, and hot desks. Members book from their portal; operators see every reservation at a glance.',
  },
  {
    icon: CreditCard,
    title: 'Automated Billing',
    description: 'Stripe-powered plans, metered overages, and invoices. Card-on-file payments, proration, and failed-payment recovery — all built in.',
  },
  {
    icon: KeyRound,
    title: 'Unifi Access Control',
    description: 'Door access auto-provisioned on signup, suspended on pause, revoked on cancel. Real-time access logs synced from your Unifi Access controller.',
  },
  {
    icon: Printer,
    title: 'Print Quota Management',
    description: 'HP EWS integration tracks B&W and color page counts per member. Quotas baked into every plan; overages billed automatically via Stripe metered usage.',
  },
  {
    icon: UserCircle,
    title: 'Member Self-Service',
    description: 'Branded portal where members book rooms, view invoices, pay cards on file, check print quotas, and see their access log — without emailing you.',
  },
  {
    icon: UserCheck,
    title: 'Visitor Management',
    description: 'Pre-register guests, send digital check-in links, and keep a searchable visitor log. Hosts get notified on arrival; front-desk workflows without the clipboard.',
  },
  {
    icon: Globe,
    title: 'Website Builder',
    description: 'Launch a public website for your space in minutes. Pick a template, edit sections inline, connect a custom domain — all from the operator dashboard.',
  },
];

const ECOSYSTEM = [
  { name: 'Stripe', desc: 'Payments, subscriptions, and metered usage billing', url: 'https://stripe.com', color: '#635bff', Icon: CreditCard },
  { name: 'Unifi Access', desc: 'Door access auto-provisioned with every membership', url: 'https://ui.com/door-access', color: '#00a5e3', Icon: KeyRound },
  { name: 'The One PSA', desc: 'Ticketing and client management for MSPs', url: 'https://theonepsa.com', color: '#3b82f6', Icon: Layers },
  { name: 'The One Voice', desc: 'Phone service as a member add-on', url: 'https://theonevoice.app', color: '#06b6d4', Icon: Phone },
  { name: 'The One Hub', desc: 'Single sign-on across every product', url: 'https://theonestack.com', color: '#a855f7', Icon: Sparkles },
];

const PRICING = [
  {
    name: 'Starter',
    price: '$49',
    cadence: '/mo',
    tagline: 'For single-location spaces getting off the ground.',
    limit: 'Up to 25 members',
    highlighted: false,
    features: [
      'Member lifecycle + Stripe billing',
      'Room & desk booking',
      'Unifi Access integration',
      'Print quota tracking',
      'Member self-service portal',
      'Visitor management',
      'Website builder',
      'Email support',
    ],
    cta: 'Start Free Trial',
  },
  {
    name: 'Growth',
    price: '$99',
    cadence: '/mo',
    tagline: 'For growing spaces with an active member base.',
    limit: 'Up to 100 members',
    highlighted: true,
    features: [
      'Everything in Starter',
      'Day passes & drop-ins',
      'Locker management',
      'Custom domain for website',
      'Phone add-on (via The One Voice)',
      'Bulk member import',
      'Priority email support',
    ],
    cta: 'Start Free Trial',
  },
  {
    name: 'Scale',
    price: '$199',
    cadence: '/mo',
    tagline: 'For multi-location operators and MSPs running spaces.',
    limit: 'Unlimited members',
    highlighted: false,
    features: [
      'Everything in Growth',
      'Unlimited members & locations',
      'API access',
      'White-label branding',
      'SSO (SAML / OIDC)',
      'Dedicated success manager',
      'Phone & chat support',
    ],
    cta: 'Start Free Trial',
  },
];

export function LandingPage() {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Nav */}
      <nav className="sticky top-0 z-40 border-b border-zinc-800/50 bg-zinc-950/80 backdrop-blur">
        <div className="mx-auto max-w-7xl flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-600">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-semibold">The One Space</span>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <a href="#features" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors">Features</a>
            <a href="#pricing" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors">Pricing</a>
            <a href="#ecosystem" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors">Ecosystem</a>
            <a href={LOGIN_URL} className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors">Log In</a>
          </div>
          <a
            href={SIGNUP_URL}
            className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500 transition-colors"
          >
            Get Started <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-violet-600/10 via-transparent to-transparent" />
        <div className="absolute left-1/2 top-20 -z-0 h-[500px] w-[500px] -translate-x-1/2 rounded-full bg-violet-600/20 blur-[120px]" />
        <div className="relative mx-auto max-w-7xl px-6 py-24 text-center lg:py-32">
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="inline-flex items-center gap-2 rounded-full bg-violet-600/10 border border-violet-600/20 px-4 py-1.5 text-sm text-violet-400">
              <Zap className="h-3.5 w-3.5" />
              Part of The One Stack ecosystem
            </div>
          </div>
          <h1 className="text-5xl font-bold tracking-tight sm:text-6xl lg:text-7xl">
            Run your coworking space.{' '}
            <span className="text-violet-400">Automatically.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-zinc-400 leading-relaxed">
            Members, bookings, billing, door access, and printing — managed from one dashboard. Signups provision Stripe, Unifi, and quotas in seconds. Built for MSPs and space operators.
          </p>
          <div className="mt-10 flex items-center justify-center gap-4 flex-wrap">
            <a
              href={SIGNUP_URL}
              className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-6 py-3 font-medium text-white hover:bg-violet-500 transition-colors"
            >
              Start Free Trial <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#features"
              className="inline-flex items-center gap-2 rounded-lg border border-zinc-700 px-6 py-3 font-medium text-zinc-300 hover:bg-zinc-800 transition-colors"
            >
              See Demo
            </a>
          </div>
          <p className="mt-6 text-sm text-zinc-500">No credit card required. 14-day free trial.</p>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="mx-auto max-w-7xl px-6 py-24">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold sm:text-4xl">Every side of running a space, in one product</h2>
          <p className="mt-4 text-lg text-zinc-400 max-w-2xl mx-auto">
            From the first signup to the last invoice — automated, integrated, and out of your way.
          </p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((feature) => (
            <div
              key={feature.title}
              className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-6 backdrop-blur transition-colors hover:border-violet-600/30"
            >
              <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-violet-600/10">
                <feature.icon className="h-5 w-5 text-violet-400" />
              </div>
              <h3 className="mb-2 font-semibold">{feature.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Ecosystem */}
      <section id="ecosystem" className="mx-auto max-w-7xl px-6 py-24">
        <div className="text-center mb-14">
          <h2 className="text-3xl font-bold sm:text-4xl">Connected to the tools you already run on</h2>
          <p className="mt-4 text-lg text-zinc-400">
            Space plugs into payments, access control, and the rest of The One Stack — no duct tape required.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-10">
          {ECOSYSTEM.map((product) => (
            <a
              key={product.name}
              href={product.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/50 p-5 hover:border-zinc-600 transition-colors"
            >
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                style={{ background: `${product.color}1a` }}
              >
                <product.Icon className="h-5 w-5" style={{ color: product.color }} />
              </div>
              <div>
                <div className="font-medium text-sm">{product.name}</div>
                <div className="text-xs text-zinc-400 mt-0.5">{product.desc}</div>
              </div>
            </a>
          ))}
        </div>
        <div className="text-center">
          <a
            href="https://theonestack.com"
            className="inline-flex items-center gap-2 text-sm font-medium text-violet-400 hover:text-violet-300 transition-colors"
          >
            Explore The One Stack <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="mx-auto max-w-7xl px-6 py-24">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold sm:text-4xl">Simple pricing. No seat fees.</h2>
          <p className="mt-4 text-lg text-zinc-400">
            Every plan includes the full product. Pick the member cap that fits your space today — upgrade anytime.
          </p>
        </div>
        <div className="grid gap-8 lg:grid-cols-3 max-w-6xl mx-auto">
          {PRICING.map((tier) => (
            <div
              key={tier.name}
              className={
                tier.highlighted
                  ? 'relative rounded-xl border border-violet-600/40 bg-gradient-to-b from-violet-600/10 to-zinc-900/50 p-8 shadow-lg shadow-violet-600/10'
                  : 'relative rounded-xl border border-zinc-800 bg-zinc-900/50 p-8'
              }
            >
              {tier.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-violet-600 px-3 py-1 text-xs font-semibold text-white">
                  Most Popular
                </div>
              )}
              <h3 className="text-xl font-bold">{tier.name}</h3>
              <p className="mt-2 text-sm text-zinc-400">{tier.tagline}</p>
              <div className="mt-6 flex items-baseline gap-1">
                <span className="text-5xl font-bold">{tier.price}</span>
                <span className="text-zinc-400">{tier.cadence}</span>
              </div>
              <p className="mt-2 text-sm font-medium text-violet-400">{tier.limit}</p>
              <ul className="mt-8 space-y-3">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3 text-sm">
                    <Check
                      className={
                        tier.highlighted
                          ? 'h-4 w-4 text-violet-400 shrink-0 mt-0.5'
                          : 'h-4 w-4 text-zinc-400 shrink-0 mt-0.5'
                      }
                    />
                    <span className="text-zinc-300">{feature}</span>
                  </li>
                ))}
              </ul>
              <a
                href={SIGNUP_URL}
                className={
                  tier.highlighted
                    ? 'mt-8 block w-full rounded-lg bg-violet-600 py-3 text-center text-sm font-medium text-white hover:bg-violet-500 transition-colors'
                    : 'mt-8 block w-full rounded-lg border border-zinc-700 py-3 text-center text-sm font-medium text-zinc-300 hover:bg-zinc-800 transition-colors'
                }
              >
                {tier.cta}
              </a>
            </div>
          ))}
        </div>
        <p className="mt-10 text-center text-sm text-zinc-500">
          Need a multi-location or white-label plan? <a href="mailto:sales@theonestack.com" className="text-violet-400 hover:text-violet-300 underline underline-offset-2">Get in touch</a>.
        </p>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-zinc-800">
        <div className="absolute inset-0 bg-gradient-to-b from-violet-600/10 via-transparent to-transparent" />
        <div className="relative mx-auto max-w-4xl px-6 py-24 text-center">
          <h2 className="text-3xl font-bold sm:text-4xl">Ready to automate your space?</h2>
          <p className="mt-4 text-lg text-zinc-400">
            Start your free 14-day trial. No credit card required. Cancel anytime.
          </p>
          <div className="mt-8 flex items-center justify-center gap-4 flex-wrap">
            <a
              href={SIGNUP_URL}
              className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-6 py-3 font-medium text-white hover:bg-violet-500 transition-colors"
            >
              Start Free Trial <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href={LOGIN_URL}
              className="inline-flex items-center gap-2 rounded-lg border border-zinc-700 px-6 py-3 font-medium text-zinc-300 hover:bg-zinc-800 transition-colors"
            >
              Log In
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800">
        <div className="mx-auto max-w-7xl px-6 py-12">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-600">
                <Building2 className="h-4 w-4 text-white" />
              </div>
              <span className="font-semibold">The One Space</span>
            </div>
            <div className="flex flex-wrap items-center gap-6 text-sm text-zinc-500">
              <a href="https://theonestack.com/privacy" className="hover:text-zinc-300 transition-colors">Privacy</a>
              <a href="https://theonestack.com/terms" className="hover:text-zinc-300 transition-colors">Terms</a>
              <a href="/cookies" className="hover:text-zinc-300 transition-colors">Cookies</a>
              <a href="https://theonestack.com" className="hover:text-zinc-300 transition-colors">Part of The One Stack</a>
            </div>
          </div>
          <div className="mt-8 border-t border-zinc-900 pt-6 text-xs text-zinc-600">
            &copy; {new Date().getFullYear()} The One Family, LLC. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
